import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  alpha,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { format, formatDistanceToNowStrict } from "date-fns";
import { tr } from "date-fns/locale";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowChip, RowEditorLine, RowMetaText } from "./_rowParts";
import ConfirmDialog from "../../common/ConfirmDialog";
import InvestigationEditor from "./InvestigationEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import {
  useListTetkikByPatientMutation,
  useDeleteTetkikMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const TYPE_LABELS = {
  laboratory: "Laboratuvar",
  imaging: "Görüntüleme",
};

const STATUS_META = {
  requested: { label: "İstendi", bg: `${COLORS.slate300}33`, fg: COLORS.slate700 },
  in_progress: { label: "Yapılıyor", bg: alpha(COLORS.warning, 0.12), fg: COLORS.warningDarker },
  completed: { label: "Tamamlandı", bg: alpha(COLORS.success, 0.08), fg: COLORS.successDark },
  cancelled: { label: "İptal", bg: alpha(COLORS.error, 0.08), fg: COLORS.errorDark },
};

const ROLE_LABEL = {
  Admin: "Yönetici",
  AdminStaff: "Admin",
  ClinicStaff: "Klinik",
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "d MMM yyyy", { locale: tr });
  } catch (_) {
    return "—";
  }
};

const formatEditor = (user) => {
  if (!user) return null;
  const title = user.title ? `${user.title} ` : "";
  const full = `${title}${user.name || ""} ${user.surname || ""}`.trim();
  return {
    name: full || "—",
    role: ROLE_LABEL[user.role] || user.role || "",
  };
};

const formatRelativeTr = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNowStrict(new Date(value), {
      addSuffix: true,
      locale: tr,
    });
  } catch (_) {
    return "";
  }
};

const summarizeItems = (item) => {
  if (item.type === "laboratory") {
    const items = item.laboratoryItems || [];
    return items.map((i) => i.name || i.labTest?.name || "—").join(", ");
  }
  if (item.type === "imaging") {
    const items = item.imagingItems || [];
    return items
      .map((i) => i.label || i.name || i.image?.name || "—")
      .join(", ");
  }
  return "";
};

/**
 * InvestigationTab — master-detail inline edit (Anamnez pattern'i ile aynı).
 */
export default function InvestigationTab({
  mode,
  patientId,
  token,
  canEdit = true,
  initialData,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onRecordChanged,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // Local alert (section-internal toast).
  const [localAlert, setLocalAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback((message, severity = "success", duration = 3500) => {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setLocalAlert({ open: true, severity, message });
    alertTimerRef.current = setTimeout(() => {
      setLocalAlert((a) => ({ ...a, open: false }));
      alertTimerRef.current = null;
    }, duration);
  }, []);
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(
    () => () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    },
    []
  );
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback((m) => showLocal(m, "error", 5000), [showLocal]);

  const [listTetkik, { isLoading: isLoadingList }] =
    useListTetkikByPatientMutation();
  const [deleteTetkik] = useDeleteTetkikMutation();

  const [list, setList] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const examinationId = initialData?.examinationId;
  const TAB_KEY = "examinations";

  // ── Hydrate list ───────────────────────────────────────────
  useEffect(() => {
    if (mode === "session") {
      setList(Array.isArray(initialData?.list) ? initialData.list : []);
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.list]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      const res = await listTetkik({ token, patientId }).unwrap();
      setList(res?.data?.list || []);
    } catch (_) {
      showError("Tetkik kayıtları yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [mode, token, patientId, listTetkik, showError]);

  useEffect(() => {
    if (mode === "standalone") refresh();
  }, [mode, refresh]);

  // ── Restore selection from localStorage (session only) ─────
  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    if (!initialLoaded || list.length === 0) return;
    const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
    if (restored && list.some((r) => r._id === restored)) {
      setSelectedId(restored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, initialLoaded]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  const selected = useMemo(
    () => list.find((r) => r._id === selectedId) || null,
    [list, selectedId]
  );

  const handleSelect = (id) => {
    setCreatingNew(false);
    setSelectedId(id);
  };

  // Yeni kayda geçmeden önceki seçim — vazgeçilince geri dönülür
  const prevSelectedRef = useRef(null);

  const handleNew = () => {
    if (!canEdit) return;
    prevSelectedRef.current = selectedId;
    setSelectedId(null);
    setCreatingNew(true);
  };

  // Kaydetmeden yeni tetkik akışından çık
  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(prevSelectedRef.current);
  };

  const handleCreated = (created) => {
    setList((prev) => [created, ...prev]);
    setSelectedId(created._id);
    setCreatingNew(false);
    if (typeof onRecordChanged === "function") onRecordChanged();
    showSuccess("Tetkik oluşturuldu.");
  };

  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setList((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    showSuccess("Tetkik güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleDelete = (rec) => {
    if (!rec?._id) return;
    setPendingDelete(rec);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      const promise = deleteTetkik({
        token,
        patientId,
        tetkikId: pendingDelete._id,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      setList((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      if (selectedId === pendingDelete._id) setSelectedId(null);
      showSuccess("Tetkik silindi.");
      if (typeof onRecordChanged === "function") onRecordChanged();
    } catch (_) {
      showError("Tetkik silinemedi.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      if (status === 423) {
        showError(
          err?.data?.message ||
            `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`
        );
        return;
      }
      showError("Tetkik kaydedilemedi.");
    },
    [showError, terms]
  );

  // ── Render ─────────────────────────────────────────────────
  const showLoading = mode === "standalone" && !initialLoaded && isLoadingList;

  // Yeni tetkik oluşturulurken buton gizlenir (Tedaviler / Anamnez ile aynı)
  const headerAction = canEdit && !creatingNew ? (
    <ActionButton
      variant="add"
      compact
      palette={palette}
      onClick={handleNew}
      aria-label="Yeni tetkik ekle"
      label="Yeni Tetkik Oluştur"
    />
  ) : null;

  const showEditor = creatingNew || Boolean(selected);

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Tetkik Kayıtları"
        count={list.length}
        action={headerAction}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : list.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<AssignmentIcon />}
            title="Henüz tetkik kaydı yok"
            lead={`${terms.patientGen} ilk tetkik isteğini`}
            tail="Laboratuvar tahlilleri ve görüntülemeler buradan istenir."
            readOnlyText={`Bu ${terms.patientLower} için oluşturulmuş tetkik kaydı bulunmuyor.`}
            onCreate={canEdit ? handleNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {creatingNew && (
                  <CreatingRow
                    label="Yeni tetkik oluşturuluyor…"
                    hint={
                      mode === "standalone"
                        ? "Tahlil veya görüntü ekleyip Kaydet'e basın"
                        : "Tahlil veya görüntü eklenince kaydedilir"
                    }
                    onCancel={handleCancelNew}
                    palette={palette}
                  />
                )}
                    {list.map((item) => {
                      const active = item._id === selectedId;
                      const typeLabel = TYPE_LABELS[item.type] || "Tetkik";
                      const statusKey = String(item.status || "requested").toLowerCase();
                      const statusMeta = STATUS_META[statusKey] || STATUS_META.requested;
                      const summary = summarizeItems(item);
                      return (
                        <SelectableListRow
                          key={item._id}
                          active={active}
                          onClick={() => handleSelect(item._id)}
                          sx={LIST_ROW_SX}
                          title={summary || "—"}
                          subtitle={
                            <>
                              <RowLine flex sx={{ mt: 0.25 }}>
                                <RowChip
                                  label={typeLabel}
                                  sx={{
                                    fontSize: "0.65rem",
                                    bgcolor: alpha(palette?.primary || COLORS.slate700, 0.1),
                                    color: palette?.primary || COLORS.slate700,
                                  }}
                                />
                                <RowChip
                                  label={statusMeta.label}
                                  sx={{
                                    fontSize: "0.65rem",
                                    bgcolor: statusMeta.bg,
                                    color: statusMeta.fg,
                                  }}
                                />
                              </RowLine>
                              <RowEditorLine
                                editor={formatEditor(item.updatedBy || item.createdBy)}
                                rel={formatRelativeTr(item.updatedAt || item.createdAt)}
                              />
                            </>
                          }
                          trailing={<RowMetaText>{formatCreatedAt(item.createdAt)}</RowMetaText>}
                        />
                      );
                    })}
              </Stack>
            }
            editor={
              !showEditor ? (
                <SelectPrompt
                  icon={<AssignmentIcon />}
                  title="Bir tetkik kaydı seçin"
                  description="Detayları görmek ve düzenlemek için soldaki listeden bir kayıt seçin."
                />
              ) : (
                <InvestigationEditor
                  key={selected?._id || "new"}
                  record={selected}
                  mode={mode}
                  patientId={patientId}
                  token={token}
                  examinationId={examinationId}
                  canEdit={canEdit}
                  onCreated={handleCreated}
                  onSaved={handleSaved}
                  onDelete={handleDelete}
                  registerAutoSave={registerAutoSave}
                  registerFlush={registerFlush}
                  onSaveStatusChange={onSaveStatusChange}
                  onError={handleEditorError}
                />
              )
            }
          />
        )}
      </WorkspaceBody>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Tetkik kaydını sil"
        message="Bu tetkik kaydı kalıcı olarak silinecek. Devam edilsin mi?"
        confirmText="Sil"
        cancelText="Vazgeç"
        loading={deleteLoading}
      />
    </Box>
  );
}
